import * as Sentry from '@sentry/react';
import { CHECK_TYPE, CHECK_TYPE_SELECT, CHECK_TYPES_DISABLED, getCheckTypeFromSlug } from '@solid/shared';
import { LoadingSpinner } from '@solid/shared/ui';
import { useQuery } from '@tanstack/react-query';
import { makeWidget } from '@typeform/embed';
import ms from 'ms';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { getCheckResult, getSignedTypeformUrl, getCheckFromDatabase } from '../../services/check.service';
import Layout from '../global/Layout';
import CheckIndividualTest from './invidiual-test/CheckIndividualTest';
import CheckIndividualMultiTest from './individual-multi-test/CheckIndividualMultiTest';
import NotificationMessage, { NotificationMessageProps } from '@solid/shared/ui/NotificationMessage';
import * as Styled from './NewCheckPage.styles';
import { Trans, useTranslation } from 'react-i18next';
import CheckIframe from './iframe/CheckIframe';

const NewCheckPage: React.FC = () => {
  const [responseId, setResponseId] = useState<string>();
  const [iframeUrl, setIframeUrl] = useState<string>();
  const [errorGettingCheck, setErrorGettingCheck] = useState(false);
  const { checkType: checkTypeAsSlug } = useParams<{ checkType: string }>();
  const [checkup, setCheckup] = useState<any>();
  const [signedUrl, setSignedUrl] = useState<string>();
  const ref = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string>('pages.newCheck.notFound');
  const [errorType, setErrorType] = useState<NotificationMessageProps['state']>('danger');

  // const checkType = getCheckTypeFromSlug(checkTypeAsSlug);
  useEffect(() => {
    setErrorGettingCheck(false);
    setErrorMessage('pages.newCheck.notFound');
    setErrorType('danger');
    getCheckFromDatabase(checkTypeAsSlug)
      .then(data => {
        if (data) {
          const checkupData = data.data.checkup;
          setCheckup(checkupData);
          if (
            checkupData.type !== CHECK_TYPE_SELECT.INDIVIDUAL_TEST &&
            checkupData.type !== CHECK_TYPE_SELECT.INDIVIDUAL_TEST_MULTI
          ) {
            const signedUrlData = data.data.url;
            setSignedUrl(signedUrlData);

            try {
              if (checkupData.type === CHECK_TYPE_SELECT.IFRAME) {
                setIframeUrl(signedUrlData);
              } else if (checkupData.type === CHECK_TYPE_SELECT.TYPEFORM) {
                makeWidget(ref.current, signedUrlData, {
                  onSubmit: e => setResponseId(e.response_id),
                  opacity: 0,
                  buttonText: 'Prüfung starten',
                });
              }
            } catch (e) {
              setErrorGettingCheck(true);
              Sentry.captureException(e);
              console.error(e);
            }
          }
        } else {
          setErrorGettingCheck(true);
        }
      })
      .catch(err => {
        setErrorGettingCheck(true);

        if (err.status === 403) {
          setErrorMessage(`pages.newCheck.notActive.${i18n.language}`);
          setErrorType('info');
        }
      });
  }, [i18n.language]);

  // Loads result of the form, triggered upon submission
  const { data: checkResult, isLoading: isLoadingResult } = useQuery(
    ['checkResult', responseId],
    () => getCheckResult(responseId as string),
    {
      enabled: !!responseId,
      retry: true,
      retryDelay: () => ms('3sec'),
    }
  );

  if (errorGettingCheck) {
    return (
      <Layout>
        <div style={{ maxWidth: errorType === 'danger' ? '300px' : '670px', margin: '200px auto' }}>
          <NotificationMessage state={errorType}>{t(errorMessage)}</NotificationMessage>
        </div>
        <Styled.BtnContainer>
          <Link className='btn is-primary is-outlined' to='/'>
            {t('common.back')}
          </Link>
        </Styled.BtnContainer>
      </Layout>
    );
  }

  if (responseId) {
    return (
      <Layout>
        {isLoadingResult && (
          <Styled.AbsolvedContainer>
            <h1>{t('pages.newCheck.loadingResult.title')}</h1>
            <p>
              <Trans i18nKey='pages.newCheck.loadingResult.text' components={{ br: <br /> }} />
            </p>
            <LoadingSpinner />
          </Styled.AbsolvedContainer>
        )}
        {checkResult && <Navigate to={`/checks/${responseId}`} />}
      </Layout>
    );
  }

  return (
    <Layout>
      <Styled.Typeform ref={ref}></Styled.Typeform>

      {checkup?.type === CHECK_TYPE_SELECT.INDIVIDUAL_TEST && <CheckIndividualTest />}

      {checkup?.type === CHECK_TYPE_SELECT.INDIVIDUAL_TEST_MULTI && <CheckIndividualMultiTest />}

      {checkup?.type === CHECK_TYPE_SELECT.IFRAME && <CheckIframe url={iframeUrl} />}

      {checkup?.type === CHECK_TYPE_SELECT.TYPEFORM && (
        <Styled.BtnContainer>
          <Link className='btn is-primary is-outlined' to='/'>
            {t('pages.newCheck.cancel')}
          </Link>
        </Styled.BtnContainer>
      )}
    </Layout>
  );
};

export default NewCheckPage;
